import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';
import { config } from '../app/config';

/** Los mismos codigos que usa la API al cerrar. */
const CIERRE = {
  NO_SE_PRESENTO: 4001,
  TOKEN_INVALIDO: 4002,
  TOKEN_CADUCADO: 4003,
  SESION_REVOCADA: 4004,
} as const;

const ESPERA_INICIAL_MS = 1_000;
const ESPERA_MAXIMA_MS = 30_000;

function urlDelCanal(): string {
  // http -> ws, https -> wss. Mismo host y mismo puerto que la API.
  return `${config.apiUrl.replace(/^http/, 'ws')}/ws`;
}

/**
 * Mantiene abierto el canal de avisos y refresca la campanita cuando llega uno.
 *
 * ## El socket es un extra, no el canal
 *
 * La campanita sigue preguntando por HTTP cada minuto y esa consulta NO se
 * quita. Si el socket esta caido, la red del movil se corta o el portatil
 * estaba dormido, las notificaciones siguen llegando; solo tardan un poco mas.
 *
 * Quitar el sondeo «porque ya hay socket» convertiria cada fallo de red en
 * avisos perdidos que nadie ve, que es la peor forma de fallar: en silencio.
 *
 * ## Por el cable no viene el contenido
 *
 * Llega «hay algo nuevo» y este hook responde volviendo a pedir la campanita
 * por HTTP, con su token. Asi quien-ve-que se decide en un unico sitio —el
 * servidor— y no hay una segunda copia de esa regla viviendo en el navegador.
 */
export function useRealtimeNotifications(): void {
  const auth = useAuth();
  const queryClient = useQueryClient();

  const token = auth.user?.access_token ?? null;

  /*
   * El token vivo, leido en el momento de conectar.
   *
   * Va en una referencia y no en las dependencias del efecto a proposito: si
   * el efecto se reiniciara con cada renovacion silenciosa, el socket se
   * cerraria y se volveria a abrir cada cinco minutos sin necesidad.
   */
  const tokenRef = useRef<string | null>(token);
  tokenRef.current = token;

  const conectado = auth.isAuthenticated;

  useEffect(() => {
    if (!conectado) return;

    let socket: WebSocket | null = null;
    let reintento: number | null = null;
    let espera = ESPERA_INICIAL_MS;
    let cancelado = false;

    const reintentar = (ms: number): void => {
      if (cancelado) return;
      reintento = window.setTimeout(abrir, ms);
    };

    function abrir(): void {
      if (cancelado) return;

      const credencial = tokenRef.current;
      if (!credencial) {
        // Todavia no hay token: no se insiste, el efecto se reactivara cuando
        // la sesion este lista.
        reintentar(ESPERA_INICIAL_MS);
        return;
      }

      try {
        socket = new WebSocket(urlDelCanal());
      } catch {
        reintentar(espera);
        espera = Math.min(espera * 2, ESPERA_MAXIMA_MS);
        return;
      }

      socket.onopen = () => {
        // El token va en el primer MENSAJE, nunca en la URL: los query strings
        // quedan escritos en los logs de acceso del servidor.
        socket?.send(JSON.stringify({ tipo: 'auth', token: tokenRef.current }));
      };

      socket.onmessage = (evento) => {
        let mensaje: { tipo?: string };
        try {
          mensaje = JSON.parse(String(evento.data)) as { tipo?: string };
        } catch {
          return;
        }

        if (mensaje.tipo === 'listo') {
          // Conexion aceptada: se reinicia la espera para que el siguiente
          // corte vuelva a reintentar enseguida.
          espera = ESPERA_INICIAL_MS;
          return;
        }

        if (mensaje.tipo === 'notificacion') {
          void queryClient.invalidateQueries({ queryKey: ['notifications'] });
        }
      };

      socket.onclose = (evento) => {
        socket = null;
        if (cancelado) return;

        /*
         * Si cerro porque caduco el token, se reconecta enseguida: la sesion
         * esta bien y `oidc-client-ts` ya tiene uno nuevo. Es lo normal cada
         * cinco minutos, no un error.
         */
        if (evento.code === CIERRE.SESION_REVOCADA) {
          // Recheck through HTTP: 401 invokes the existing session-expired flow;
          // a temporary provider outage (503) must not erase local drafts.
          void import('./api').then(({ api }) => api.get('/api/me')).catch(() => undefined);
          reintentar(ESPERA_MAXIMA_MS);
          return;
        }
        if (evento.code === CIERRE.TOKEN_CADUCADO) {
          reintentar(ESPERA_INICIAL_MS);
          return;
        }

        /*
         * Si el token no vale, insistir cada segundo solo castiga a Keycloak
         * con verificaciones que van a fallar igual. Se espera lo maximo y se
         * deja que la renovacion de la sesion arregle el problema.
         */
        if (evento.code === CIERRE.TOKEN_INVALIDO || evento.code === CIERRE.NO_SE_PRESENTO) {
          reintentar(ESPERA_MAXIMA_MS);
          return;
        }

        reintentar(espera);
        espera = Math.min(espera * 2, ESPERA_MAXIMA_MS);
      };

      socket.onerror = () => {
        // `onclose` llega siempre detras y es quien decide el reintento; aqui
        // no hay nada que hacer salvo no dejar el error sin manejar.
      };
    }

    abrir();

    return () => {
      cancelado = true;
      if (reintento !== null) window.clearTimeout(reintento);
      // 1000 = cierre normal: no es un fallo, es que esta pantalla se va.
      socket?.close(1000, 'fin de la sesion en esta pestana');
      socket = null;
    };
  }, [conectado, queryClient]);
}
